import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { CheckCircle2, Calendar, MapPin } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function absoluteUrl(u) {
    if (!u) return null;
    if (u.startsWith("http")) return u;
    return `${BACKEND_URL}${u}`;
}

export default function CastingPrivate() {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        api.get(`/casting/private/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Invalid link"));
    }, [token]);

    const pay = async (label, amount) => {
        setBusy(true);
        try {
            const kind = label.toLowerCase().includes("deposit") ? "deposit" : "installment";
            const { data } = await api.post(`/casting/private/${token}/pay?kind=${kind}&amount_cents=${amount}`);
            window.location.assign(data.url);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Unable to open checkout");
        } finally { setBusy(false); }
    };

    if (err) return <main className="pt-40 pb-40 text-center min-h-screen"><h1 className="font-display text-6xl">{err}</h1></main>;
    if (!info) return <main className="pt-40 min-h-screen text-center text-[color:var(--cream-muted)]">Loading…</main>;

    const headshot = absoluteUrl(info.headshot_url) || (info.photo_keys?.[0] && absoluteUrl(`/api/uploads/${info.photo_keys[0]}`));
    const schedule = (info.deposit_schedule && info.deposit_schedule.length > 0)
        ? info.deposit_schedule
        : [
            { label: "Deposit", amount_cents: info.deposit_cents, due_date: "On acceptance", paid: false },
            ...Array.from({ length: info.installments_total }, (_, i) => ({ label: `Installment ${i + 1}`, amount_cents: info.installment_cents, due_date: `${(i + 1) * 30} days after acceptance`, paid: false })),
        ];

    return (
        <main className="pt-32 pb-32 min-h-screen" data-testid="casting-private">
            <section className="mx-auto max-w-6xl px-6 lg:px-12">
                <p className="eyebrow">Private link — For {info.first_name} {info.last_name}</p>
                <h1 className="font-display text-6xl md:text-8xl mt-4 leading-[0.95]">You're <em className="text-[color:var(--gold)]">in</em>.</h1>

                <div className="mt-16 grid lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5">
                        <div className="border border-[color:var(--gold)]/40 overflow-hidden">
                            {headshot ? (
                                <img src={headshot} alt={`${info.first_name} portrait`} className="w-full h-[560px] object-cover" data-testid="cast-headshot" />
                            ) : (
                                <div className="h-[560px] bg-[color:var(--dark-grey)] flex items-center justify-center">
                                    <span className="font-display text-8xl text-[color:var(--gold)]/40">{info.first_name?.[0]}{info.last_name?.[0]}</span>
                                </div>
                            )}
                        </div>
                        <div className="mt-6 space-y-3 text-sm">
                            {info.filming_date && (
                                <div className="flex items-center gap-3 text-[color:var(--soft-cream)]" data-testid="filming-date">
                                    <Calendar size={16} strokeWidth={1.25} className="text-[color:var(--gold)]" />
                                    <span>Filming: {new Date(info.filming_date).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</span>
                                </div>
                            )}
                            <div className="flex items-center gap-3 text-[color:var(--cream-muted)]">
                                <MapPin size={16} strokeWidth={1.25} className="text-[color:var(--gold)]" />
                                <span>Location details sent by email 48h before arrival</span>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        {info.bio && (
                            <div className="mb-12" data-testid="cast-bio">
                                <p className="eyebrow">Your story, in our records</p>
                                <p className="mt-4 font-serif text-2xl md:text-3xl leading-relaxed">{info.bio}</p>
                            </div>
                        )}

                        <p className="eyebrow">Your payment plan</p>
                        <p className="mt-4 text-[color:var(--cream-muted)] max-w-lg">Personalised to you. Secure your seat with the deposit; the remaining balance follows the schedule below.</p>

                        <div className="mt-10 space-y-3" data-testid="deposit-schedule">
                            {schedule.map((s, i) => (
                                <div key={`${s.label}-${s.due_date}-${i}`} className={`border p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 ${s.paid ? "border-[color:var(--gold)] bg-[color:var(--dark-grey)]" : "border-[color:var(--espresso)]"}`} data-testid={`schedule-item-${i}`}>
                                    <div>
                                        <div className="flex items-center gap-3">
                                            <span className="font-serif text-2xl">{s.label}</span>
                                            {s.paid && <CheckCircle2 size={18} strokeWidth={1.5} className="text-[color:var(--gold)]" />}
                                        </div>
                                        <p className="text-xs text-[color:var(--cream-muted)] mt-1 tracking-[0.16em] uppercase">{s.due_date}</p>
                                    </div>
                                    <div className="flex items-center gap-6">
                                        <span className="font-serif text-3xl text-[color:var(--gold)]">${(s.amount_cents / 100).toFixed(0)}</span>
                                        {!s.paid && (
                                            <button onClick={() => pay(s.label, s.amount_cents)} disabled={busy} className={i === 0 ? "btn-gold btn-gold-solid" : "btn-gold"} data-testid={`pay-${i}`}>
                                                {busy ? "Opening…" : i === 0 ? "Pay deposit" : "Pay early"}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <p className="mt-10 text-xs text-[color:var(--cream-muted)] leading-relaxed max-w-lg">
                            Secure payment by Square. Each payment is receipted to {info.email}. This link is private to you and remains active through the full filming period.
                        </p>
                    </div>
                </div>
            </section>
        </main>
    );
}
